import type { SuperHubDocumentData } from '.';
import type { ReadChangelogType } from '@readme/api-contracts/schemas/changelog';
import type { CustomPageReadType } from '@readme/api-contracts/schemas/custompage';
import type { ReadDiscussType } from '@readme/api-contracts/schemas/discuss';
import type { RecipeReadType } from '@readme/api-contracts/schemas/recipe';
import type { ReadGuideType } from '@readme/api/mappings/page/guide/types';
import type { ReadReferenceType } from '@readme/api/mappings/page/reference/types';
import type { PartialDeep } from 'type-fest';

/**
 * Returns whether the provided document is a guides page type or not.
 */
export function isGuidesPage(document?: PartialDeep<SuperHubDocumentData> | null): document is ReadGuideType['data'] {
  if (!document) return false;
  const guidesPage = document as ReadGuideType['data'];
  return ['link', 'basic'].includes(guidesPage.type ?? '') && !('api' in guidesPage);
}

/**
 * Returns whether the provided document is a reference page type or not.
 */
export function isReferencePage(
  document?: PartialDeep<SuperHubDocumentData> | null,
): document is ReadReferenceType['data'] {
  if (!document) return false;
  const referencePage = document as ReadReferenceType['data'];
  return 'api' in referencePage;
}

/**
 * Returns whether the provided document is a custom page type or not.
 */
export function isCustomPage(
  document?: PartialDeep<SuperHubDocumentData> | null,
): document is CustomPageReadType['data'] {
  if (!document) return false;
  const customPage = document as CustomPageReadType['data'];
  return ['html', 'markdown'].includes(customPage.content?.type ?? '');
}

/**
 * Returns whether the provided document is a changelog type or not.
 */
export function isChangelog(
  document?: PartialDeep<SuperHubDocumentData> | null,
): document is ReadChangelogType['data'] {
  if (!document) return false;
  const changelog = document as ReadChangelogType['data'];
  return ['none', 'added', 'fixed', 'improved', 'deprecated', 'removed'].includes(changelog.type ?? '');
}

/**
 * Returns whether the provided document is a recipe type or not.
 * Accepts a broader input type since recipes are not part of SuperHubDocumentData.
 */
export function isRecipe(
  document?: PartialDeep<SuperHubDocumentData> | RecipeReadType['data'] | null,
): document is RecipeReadType['data'] {
  if (!document) return false;
  const recipe = document as RecipeReadType['data'];
  return Array.isArray(recipe.content?.steps);
}

/**
 * Returns whether the provided document is a discussion type or not.
 * Accepts a broader input type since discussions are not part of SuperHubDocumentData.
 */
export function isDiscussion(
  document?: PartialDeep<SuperHubDocumentData> | ReadDiscussType['data'] | null,
): document is ReadDiscussType['data'] {
  if (!document) return false;
  const discussion = document as ReadDiscussType['data'];
  return 'solved' in discussion && 'is_faq' in discussion;
}

/**
 * Returns whether the provided document is a API Config (formerly Realtime) page type or not.
 */
export function isAPIConfigPage(
  document?: PartialDeep<SuperHubDocumentData> | null,
): document is ReadReferenceType['data'] {
  if (!document) return false;
  const apiConfigPage = document as ReadReferenceType['data'];
  return apiConfigPage.type === 'api_config';
}

/**
 * Stable JSON snapshot of editable fields in the document.
 */
function serializeRemoteComparableDocument(data: SuperHubDocumentData): string {
  let comparable: unknown;

  if (isChangelog(data)) {
    comparable = {
      title: data.title,
      content: data.content,
      type: data.type,
      privacy: data.privacy,
    };
  } else if (isCustomPage(data)) {
    comparable = {
      title: data.title,
      content: data.content,
      privacy: data.privacy,
    };
  } else if (isReferencePage(data)) {
    comparable = {
      content: data.content,
      type: data.type,
      state: data.state,
      privacy: data.privacy,
      appearance: data.appearance,
    };
  } else if (isGuidesPage(data)) {
    comparable = {
      title: data.title,
      content: data.content,
      type: data.type,
      state: data.state,
      privacy: data.privacy,
      appearance: data.appearance,
    };
  }

  return JSON.stringify(comparable);
}

/** True when the remote document differs in any author-editable field we track (including `title`). */
export function hasRemoteDocumentEditableConflict(
  current: SuperHubDocumentData,
  incoming: SuperHubDocumentData,
): boolean {
  return serializeRemoteComparableDocument(current) !== serializeRemoteComparableDocument(incoming);
}
